/**
 * Hot Deals Page - filterable table of hot/warming deals.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ArrowLeft, Loader2, Flame, Search } from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import momentumService from '../services/momentumService';
import { HotDealEntry, MomentumTier } from '../types/dealMomentum';
import MomentumBadge from '../components/MomentumBadge';
import VelocityIndicator from '../components/VelocityIndicator';

function formatCurrency(n?: number | null): string {
  if (n == null) return '$0';
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
  return `$${Math.round(n)}`;
}

function entityName(d: HotDealEntry): string {
  if (d.entity_name) return d.entity_name;
  if (d.project?.project_name) return d.project.project_name;
  if (d.startup?.name) return d.startup.name;
  if (d.project_id) return `Project #${d.project_id}`;
  if (d.startup_id) return `Startup #${d.startup_id}`;
  return `Deal #${d.id}`;
}

function entityLink(d: HotDealEntry): string {
  const kind = d.entity_kind || (d.project_id ? 'project' : 'startup');
  const eid = d.project_id || d.startup_id || 0;
  return `/momentum/${kind}/${eid}`;
}

const TIER_FILTER_OPTIONS: Array<{ value: '' | MomentumTier; label: string }> = [
  { value: '', label: 'All tiers' },
  { value: 'hot', label: 'Hot' },
  { value: 'warming', label: 'Warming' },
  { value: 'steady', label: 'Steady' },
  { value: 'cooling', label: 'Cooling' },
  { value: 'cold', label: 'Cold' },
];

const HotDealsPage: React.FC = () => {
  const [deals, setDeals] = useState<HotDealEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [tierFilter, setTierFilter] = useState<'' | MomentumTier>('');
  const [search, setSearch] = useState('');

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const res = await momentumService.fetchHotDeals(100);
      if (res.error) {
        toast.error(res.error);
      } else {
        setDeals(res.data || []);
      }
      setLoading(false);
    };
    load();
  }, []);

  const filtered = useMemo(() => {
    return deals.filter((d) => {
      if (tierFilter && d.momentum_tier !== tierFilter) return false;
      if (search && !entityName(d).toLowerCase().includes(search.toLowerCase()))
        return false;
      return true;
    });
  }, [deals, tierFilter, search]);

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <Link
          to="/momentum"
          className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back to momentum
        </Link>

        <div>
          <h1 className="text-2xl font-bold text-gray-900 inline-flex items-center">
            <Flame className="w-6 h-6 mr-2 text-red-500" />
            Hot deals
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            Deals with elevated investor signal. Filter by tier.
          </p>
        </div>

        <div className="flex flex-col md:flex-row gap-2 items-stretch md:items-center">
          <div className="relative flex-1 max-w-md">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="w-4 h-4 text-gray-400" />
            </div>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search entities..."
              className="pl-10 pr-3 py-2 w-full border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
          <select
            value={tierFilter}
            onChange={(e) => setTierFilter(e.target.value as any)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            {TIER_FILTER_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>

        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          {loading ? (
            <div className="p-10 flex items-center justify-center text-gray-600">
              <Loader2 className="w-5 h-5 mr-3 animate-spin" />
              Loading...
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-8 text-center text-gray-500 text-sm">
              No deals match.
            </div>
          ) : (
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-3 py-2 font-medium text-gray-700">
                    Entity
                  </th>
                  <th className="text-left px-3 py-2 font-medium text-gray-700">
                    Tier
                  </th>
                  <th className="text-right px-3 py-2 font-medium text-gray-700">
                    Velocity
                  </th>
                  <th className="text-right px-3 py-2 font-medium text-gray-700">
                    Views (week)
                  </th>
                  <th className="text-right px-3 py-2 font-medium text-gray-700">
                    Matches
                  </th>
                  <th className="text-right px-3 py-2 font-medium text-gray-700">
                    Soft $
                  </th>
                  <th className="text-right px-3 py-2 font-medium text-gray-700">
                    Round %
                  </th>
                  <th className="text-right px-3 py-2 font-medium text-gray-700">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.map((d) => (
                  <tr key={d.id}>
                    <td className="px-3 py-2">
                      <div className="font-medium text-gray-900">
                        {entityName(d)}
                      </div>
                      <div className="text-xs text-gray-500 capitalize">
                        {d.entity_kind ||
                          (d.project_id ? 'project' : 'startup')}
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <MomentumBadge tier={d.momentum_tier} size="sm" />
                    </td>
                    <td className="px-3 py-2 text-right">
                      <VelocityIndicator
                        velocity={d.velocity_score}
                        size="sm"
                      />
                    </td>
                    <td className="px-3 py-2 text-right text-gray-700">
                      {d.views_this_week}
                    </td>
                    <td className="px-3 py-2 text-right text-gray-700">
                      {d.match_requests}
                    </td>
                    <td className="px-3 py-2 text-right text-gray-700">
                      {formatCurrency(d.soft_commitment_total)}
                    </td>
                    <td className="px-3 py-2 text-right text-gray-700">
                      {Math.round(d.round_progress_percent)}%
                    </td>
                    <td className="px-3 py-2 text-right">
                      <Link
                        to={entityLink(d)}
                        className="text-blue-600 hover:underline text-xs"
                      >
                        View
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default HotDealsPage;
